import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, RefreshCw, Save, Send } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/page-header";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { apiFetch } from "@/lib/api";
import { formatDateTime } from "@/lib/utils";

interface EmailRow {
  id: string;
  toAddress: string;
  subject: string;
  status: string;
  attempts: number;
  lastError: string | null;
  createdAt: string;
  sentAt: string | null;
}

interface EmailConfig {
  provider: "disabled" | "graph" | "smtp";
  enabled: boolean;
  configured: boolean;
  sender: string | null;
  host: string | null;
  port: number | null;
  secureTransport: string | null;
  missing: string[];
  source: string;
  hasGraphSecret: boolean;
  hasSmtpSecret: boolean;
  graphTenantId: string | null;
  graphClientId: string | null;
  graphSender: string | null;
  smtpHost: string | null;
  smtpPort: number | null;
  smtpSecure: string | null;
  smtpUsername: string | null;
  smtpFrom: string | null;
  smtpHeloName: string | null;
  replyTo: string | null;
  recent: EmailRow[];
}

interface EmailForm {
  provider: "disabled" | "graph" | "smtp";
  enabled: boolean;
  graphTenantId: string;
  graphClientId: string;
  graphClientSecret: string;
  graphSender: string;
  smtpHost: string;
  smtpPort: string;
  smtpSecure: "tls" | "starttls";
  smtpUsername: string;
  smtpPassword: string;
  smtpFrom: string;
  smtpHeloName: string;
  replyTo: string;
}

const EMPTY_FORM: EmailForm = {
  provider: "disabled",
  enabled: false,
  graphTenantId: "",
  graphClientId: "",
  graphClientSecret: "",
  graphSender: "",
  smtpHost: "",
  smtpPort: "587",
  smtpSecure: "starttls",
  smtpUsername: "",
  smtpPassword: "",
  smtpFrom: "",
  smtpHeloName: "",
  replyTo: "",
};

export function SettingsEmailPage() {
  const queryClient = useQueryClient();
  const [to, setTo] = useState("");
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<EmailForm>(EMPTY_FORM);
  const config = useQuery({
    queryKey: ["email-config"],
    queryFn: () => apiFetch<EmailConfig>("/api/v1/admin/email/config"),
  });
  const data = config.data;

  useEffect(() => {
    if (!data) return;
    setForm({
      provider: data.provider,
      enabled: data.enabled,
      graphTenantId: data.graphTenantId ?? "",
      graphClientId: data.graphClientId ?? "",
      graphClientSecret: "",
      graphSender: data.graphSender ?? "",
      smtpHost: data.smtpHost ?? "",
      smtpPort: String(data.smtpPort ?? 587),
      smtpSecure: data.smtpSecure === "tls" ? "tls" : "starttls",
      smtpUsername: data.smtpUsername ?? "",
      smtpPassword: "",
      smtpFrom: data.smtpFrom ?? "",
      smtpHeloName: data.smtpHeloName ?? "",
      replyTo: data.replyTo ?? "",
    });
  }, [data]);

  async function saveSettings() {
    setSaving(true);
    try {
      await apiFetch<EmailConfig>("/api/v1/admin/email/settings", {
        method: "PUT",
        body: JSON.stringify({
          provider: form.provider,
          enabled: form.enabled,
          graphTenantId: form.graphTenantId || null,
          graphClientId: form.graphClientId || null,
          graphClientSecret: form.graphClientSecret || null,
          graphSender: form.graphSender || null,
          smtpHost: form.smtpHost || null,
          smtpPort: form.provider === "smtp" ? Number(form.smtpPort) : null,
          smtpSecure: form.provider === "smtp" ? form.smtpSecure : null,
          smtpUsername: form.smtpUsername || null,
          smtpPassword: form.smtpPassword || null,
          smtpFrom: form.smtpFrom || null,
          smtpHeloName: form.smtpHeloName || null,
          replyTo: form.replyTo || null,
        }),
      });
      await queryClient.invalidateQueries({ queryKey: ["email-config"] });
      toast.success("邮件设置已保存");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "保存失败");
    } finally {
      setSaving(false);
    }
  }

  async function sendTest() {
    if (!to.trim()) return toast.error("请输入收件邮箱");
    setSending(true);
    try {
      const result = await apiFetch<{ status: string; error: string | null }>(
        "/api/v1/admin/email/test",
        { method: "POST", body: JSON.stringify({ to: to.trim() }) },
      );
      if (result.status === "sent") toast.success("测试邮件已发送");
      else toast.error(`发送失败：${result.error ?? "请检查配置"}`);
      await queryClient.invalidateQueries({ queryKey: ["email-config"] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "发送失败");
    } finally {
      setSending(false);
    }
  }

  async function retry(id: string) {
    try {
      const result = await apiFetch<{ status: string; error: string | null }>(
        `/api/v1/admin/email/outbox/${id}/retry`,
        { method: "POST" },
      );
      if (result.status === "sent") toast.success("已重试发送");
      else toast.error(`重试失败：${result.error ?? "未知错误"}`);
      await queryClient.invalidateQueries({ queryKey: ["email-config"] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "重试失败");
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="邮件通知"
        description="前端配置 Exchange Online Graph 或通用 SMTP；Secret 使用 AES-GCM 加密存储，接口不返回明文。"
        actions={
          <Button variant="outline" onClick={() => config.refetch()} disabled={config.isFetching}>
            <RefreshCw className="h-4 w-4" />
            刷新
          </Button>
        }
      />

      <Alert
        variant={data?.configured ? "success" : "warning"}
        title={data?.configured ? "邮件通道已配置" : "邮件通道未就绪"}
      >
        {data
          ? data.configured
            ? data.provider === "smtp"
              ? `SMTP：${data.host}:${data.port}（${data.secureTransport === "tls" ? "隐式 TLS" : "STARTTLS"}）；发件人：${data.sender}。`
              : `Graph：${data.sender}；OAuth2 client_credentials + Mail.Send。`
            : data.provider === "disabled"
              ? "当前未启用邮件通知。选择 Graph 或 SMTP 并保存。"
              : `缺少：${data.missing.join("、")}。`
          : "加载中…"}
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>通道配置</CardTitle>
          <CardDescription>保存后立即生效；密码/客户端 Secret 留空表示保持原值。</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>邮件通道</Label>
              <select
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.provider}
                onChange={(event) =>
                  setForm((value) => ({
                    ...value,
                    provider: event.target.value as EmailForm["provider"],
                    enabled: event.target.value !== "disabled",
                  }))
                }
              >
                <option value="disabled">禁用</option>
                <option value="graph">Exchange Online (Microsoft Graph)</option>
                <option value="smtp">通用 SMTP (TLS / STARTTLS)</option>
              </select>
            </div>
            <label className="flex items-end gap-2 pb-2 text-sm">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(event) =>
                  setForm((value) => ({ ...value, enabled: event.target.checked }))
                }
              />
              启用邮件通知
            </label>
          </div>

          {form.provider === "graph" ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Tenant ID</Label>
                <Input
                  value={form.graphTenantId}
                  onChange={(event) => setForm({ ...form, graphTenantId: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Client ID</Label>
                <Input
                  value={form.graphClientId}
                  onChange={(event) => setForm({ ...form, graphClientId: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Client Secret {data?.hasGraphSecret ? "（已保存，留空不修改）" : ""}</Label>
                <Input
                  type="password"
                  value={form.graphClientSecret}
                  onChange={(event) => setForm({ ...form, graphClientSecret: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>发件邮箱</Label>
                <Input
                  value={form.graphSender}
                  onChange={(event) => setForm({ ...form, graphSender: event.target.value })}
                  placeholder="edm-noreply@contoso.com"
                />
              </div>
            </div>
          ) : null}

          {form.provider === "smtp" ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>SMTP 主机</Label>
                <Input
                  value={form.smtpHost}
                  onChange={(event) => setForm({ ...form, smtpHost: event.target.value })}
                  placeholder="smtp.example.com"
                />
              </div>
              <div className="space-y-2">
                <Label>端口</Label>
                <Input
                  type="number"
                  value={form.smtpPort}
                  onChange={(event) => setForm({ ...form, smtpPort: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>加密方式</Label>
                <select
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                  value={form.smtpSecure}
                  onChange={(event) =>
                    setForm({ ...form, smtpSecure: event.target.value as "tls" | "starttls" })
                  }
                >
                  <option value="tls">隐式 TLS（465）</option>
                  <option value="starttls">STARTTLS（587）</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>用户名</Label>
                <Input
                  value={form.smtpUsername}
                  onChange={(event) => setForm({ ...form, smtpUsername: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>密码 {data?.hasSmtpSecret ? "（已保存，留空不修改）" : ""}</Label>
                <Input
                  type="password"
                  value={form.smtpPassword}
                  onChange={(event) => setForm({ ...form, smtpPassword: event.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>发件人</Label>
                <Input
                  value={form.smtpFrom}
                  onChange={(event) => setForm({ ...form, smtpFrom: event.target.value })}
                  placeholder="EDM <edm@example.com>"
                />
              </div>
              <div className="space-y-2">
                <Label>HELO 名称（可选）</Label>
                <Input
                  value={form.smtpHeloName}
                  onChange={(event) => setForm({ ...form, smtpHeloName: event.target.value })}
                  placeholder="edm.example.com"
                />
              </div>
            </div>
          ) : null}

          <div className="space-y-2">
            <Label>Reply-To（可选）</Label>
            <Input
              value={form.replyTo}
              onChange={(event) => setForm({ ...form, replyTo: event.target.value })}
            />
          </div>

          <div className="flex items-center gap-3">
            <Button onClick={saveSettings} disabled={saving}>
              <Save className="h-4 w-4" />
              {saving ? "保存中…" : "保存设置"}
            </Button>
            <span className="text-xs text-muted-foreground">
              密码/Secret 使用 AES-GCM 加密后存入 D1，GET 接口不返回明文。
            </span>
          </div>

          {form.provider !== "disabled" ? (
            <p className="text-xs text-muted-foreground">
              {form.provider === "smtp"
                ? "SMTP 强制使用 465 隐式 TLS 或 587 STARTTLS；明文模式会被服务端拒绝。"
                : "Graph 需要 Entra 应用权限 Mail.Send 及管理员同意；不使用 SMTP AUTH。"}
            </p>
          ) : null}
        </CardContent>
      </Card>

      <div className="grid gap-6 xl:grid-cols-[1fr_1.2fr]">
        <Card>
          <CardHeader>
            <CardTitle>发送测试邮件</CardTitle>
            <CardDescription>使用当前已保存配置，写入发件箱并立即尝试发送。</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>收件邮箱</Label>
              <Input
                value={to}
                onChange={(event) => setTo(event.target.value)}
                placeholder="name@example.com"
              />
            </div>
            <Button onClick={sendTest} disabled={sending || !data?.configured}>
              <Send className="h-4 w-4" />
              {sending ? "发送中…" : "发送测试"}
            </Button>
            {!data?.configured ? (
              <p className="text-xs text-amber-700">保存完整配置并启用后才能发送。</p>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>当前状态</CardTitle>
            <CardDescription>配置来源与密钥状态。</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between border-b border-border py-2">
              <span className="text-muted-foreground">通道</span>
              <span>{data?.provider ?? "—"}</span>
            </div>
            <div className="flex justify-between border-b border-border py-2">
              <span className="text-muted-foreground">启用</span>
              <span>{data?.enabled ? "是" : "否"}</span>
            </div>
            <div className="flex justify-between border-b border-border py-2">
              <span className="text-muted-foreground">配置完整</span>
              <span>{data?.configured ? "是" : "否"}</span>
            </div>
            <div className="flex justify-between border-b border-border py-2">
              <span className="text-muted-foreground">Graph Secret</span>
              <span>{data?.hasGraphSecret ? "已保存" : "未保存"}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">SMTP 密码</span>
              <span>{data?.hasSmtpSecret ? "已保存" : "未保存"}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>发件箱</CardTitle>
          <CardDescription>最近 30 封；失败可重试，Cron 每日也会补发。</CardDescription>
        </CardHeader>
        <CardContent>
          {config.isLoading ? (
            <Skeleton className="h-48 w-full" />
          ) : (data?.recent ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">暂无邮件记录。</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>收件人</TableHead>
                  <TableHead>主题</TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead>尝试</TableHead>
                  <TableHead>时间</TableHead>
                  <TableHead className="text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.recent ?? []).map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="text-xs">{row.toAddress}</TableCell>
                    <TableCell className="max-w-64 truncate">{row.subject}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          row.status === "sent"
                            ? "success"
                            : row.status === "failed"
                              ? "danger"
                              : "warning"
                        }
                      >
                        {row.status === "sent"
                          ? "已发送"
                          : row.status === "failed"
                            ? "失败"
                            : "待发送"}
                      </Badge>
                      {row.lastError ? (
                        <p className="mt-1 max-w-64 truncate text-[10px] text-red-600">
                          {row.lastError}
                        </p>
                      ) : null}
                    </TableCell>
                    <TableCell>{row.attempts}</TableCell>
                    <TableCell className="text-xs">
                      {formatDateTime(row.sentAt ?? row.createdAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      {row.status !== "sent" ? (
                        <Button variant="ghost" size="sm" onClick={() => retry(row.id)}>
                          <Mail className="h-3.5 w-3.5" />
                          重试
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
